// Channel Web SDK를 직접 주입/호출하는 경량 래퍼 (모듈/객체 버전)

declare global {
  interface Window {
    ChannelIO?: IChannelIO;
    ChannelIOInitialized?: boolean;
  }
}

export interface IChannelIO {
  c?: (...args: any[]) => void;
  q?: [methodName: string, ...args: any[]][];
  (...args: any[]): void;
}

export type Appearance = "light" | "dark" | "system" | null;

export interface Profile {
  [key: string]: string | number | boolean | null | undefined;
}

export interface BootOption {
  appearance?: Appearance;
  customLauncherSelector?: string;
  hideChannelButtonOnBoot?: boolean;
  hidePopup?: boolean;
  language?: string;
  memberHash?: string;
  memberId?: string;
  pluginKey: string;
  profile?: Profile;
  trackDefaultEvent?: boolean;
  trackUtmSource?: boolean;
  unsubscribe?: boolean; // deprecated지만 호환 위해 유지
  unsubscribeEmail?: boolean;
  unsubscribeTexting?: boolean;
  zIndex?: number;
}

export interface CallbackUser {
  alert: number;
  avatarUrl: string;
  id: string;
  language: string;
  memberId: string;
  name?: string;
  profile?: Profile | null;
  tags?: string[] | null;
  unsubscribeEmail: boolean;
  unsubscribeTexting: boolean;
}

export type Callback = (error: Error | null, user: CallbackUser | null) => void;

export interface UpdateUserInfo {
  language?: string;
  profile?: Profile | null;
  profileOnce?: Profile;
  tags?: string[] | null;
  unsubscribeEmail?: boolean;
  unsubscribeTexting?: boolean;
}

export interface FollowUpProfile {
  name?: string | null;
  mobileNumber?: string | null;
  email?: string | null;
}

const ChannelService = {
  // ✔ class-methods-use-this 제거 / no-var 제거 / prefer-rest-params 적용 / consistent-return OK
  loadScript(): void {
    (function () {
      const w = window;

      // include twice 방지: return 값 없이 조용히 종료(consistent-return 위반 방지)
      if (w.ChannelIO) {
        w.console.error?.("ChannelIO script included twice.");
      } else {
        // 큐잉 가능한 프록시 생성
        const ch: IChannelIO = ((...args: any[]) => {
          ch.c?.(args as any);
        }) as IChannelIO;

        ch.q = [];
        ch.c = (args: any) => {
          ch.q!.push(args);
        };

        w.ChannelIO = ch;

        const load = () => {
          if (w.ChannelIOInitialized) return;
          w.ChannelIOInitialized = true;

          const s = document.createElement("script");
          s.type = "text/javascript";
          s.async = true;
          s.src = "https://cdn.channel.io/plugin/ch-plugin-web.js";

          const x = document.getElementsByTagName("script")[0];
          x?.parentNode?.insertBefore(s, x);
        };

        if (document.readyState === "complete") {
          load();
        } else {
          w.addEventListener("DOMContentLoaded", load);
          w.addEventListener("load", load);
        }
      }
      // IIFE는 아무 값도 반환하지 않음 → 모든 경로 일관됨
    })();
  },

  boot(option: BootOption, callback?: Callback): void {
    window.ChannelIO?.("boot", option, callback);
  },

  shutdown(): void {
    window.ChannelIO?.("shutdown");
  },

  showMessenger(): void {
    window.ChannelIO?.("showMessenger");
  },

  hideMessenger(): void {
    window.ChannelIO?.("hideMessenger");
  },

  openChat(chatId?: string | number, message?: string): void {
    window.ChannelIO?.("openChat", chatId, message);
  },

  track(eventName: string, eventProperty?: Record<string, any>): void {
    window.ChannelIO?.("track", eventName, eventProperty);
  },

  onShowMessenger(callback: () => void): void {
    window.ChannelIO?.("onShowMessenger", callback);
  },

  onHideMessenger(callback: () => void): void {
    window.ChannelIO?.("onHideMessenger", callback);
  },

  onBadgeChanged(callback: (unread: number, alert: number) => void): void {
    window.ChannelIO?.("onBadgeChanged", callback);
  },

  onChatCreated(callback: () => void): void {
    window.ChannelIO?.("onChatCreated", callback);
  },

  onFollowUpChanged(callback: (profile: FollowUpProfile) => void): void {
    window.ChannelIO?.("onFollowUpChanged", callback);
  },

  onUrlClicked(callback: (url: string) => void): void {
    window.ChannelIO?.("onUrlClicked", callback);
  },

  clearCallbacks(): void {
    window.ChannelIO?.("clearCallbacks");
  },

  updateUser(userInfo: UpdateUserInfo, callback?: Callback): void {
    window.ChannelIO?.("updateUser", userInfo, callback);
  },

  addTags(tags: string[], callback?: Callback): void {
    window.ChannelIO?.("addTags", tags, callback);
  },

  removeTags(tags: string[], callback?: Callback): void {
    window.ChannelIO?.("removeTags", tags, callback);
  },

  setPage(page: string): void {
    window.ChannelIO?.("setPage", page);
  },

  resetPage(): void {
    window.ChannelIO?.("resetPage");
  },

  showChannelButton(): void {
    window.ChannelIO?.("showChannelButton");
  },

  hideChannelButton(): void {
    window.ChannelIO?.("hideChannelButton");
  },

  setAppearance(appearance: Appearance): void {
    window.ChannelIO?.("setAppearance", appearance);
  },
};

export default ChannelService;
